import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { _ as GridRender } from "gridjs-react";
import { Button, Col, Row } from "react-bootstrap";
import ActionDropDown from "./ActionDropDown";
import DataGrid from "./DataGrid";
import SearchFilter from "./SearchFilter";
import SelectOption from "../SelectOption";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { PAGE } from "../../constants/page";
import "../../styles/components/Admin/PatientDetails.scss";
import { updateBreadcrum } from "../../redux/reducers/BreadcrumbSlice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { API } from "../../constants/api";
import {
  ApiCallWithLoader,
  StaticContentCallWithLoader,
  errorUpdate,
} from "../../redux/reducers/LoaderSlice";
import Utility from "../../utility/Utility";
import Locations from "../../utility/Locations";
import Pagination from "../Pagination";
import { Trans } from "react-i18next";
import { UserState } from "../../redux/reducers/UserSlice";
import i18next from "i18next";
import { PersistState } from "../../redux/reducers/PersistSlice";
import ErrorBoundaryHandler from "../ErrorBoundaryHandler";
import NameEllipses from "./NameEllipses";
import LeydenSelect from "../LeydenSelect";
import { SORT_LIST_BENIFICIARY } from "src/constants/options";
import AdminSearch from "./AdminSearch";

function PatientsDetails() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useParams();
  const [searchString, setSearchString] = useState("");

  const [districts, setDistricts] = useState<any>(null);
  const [citiesList, setCitiesList] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState<any>([]);
  const [searchPatient, setSearchPatient] = useState<any>();
  const [submitClicked, setSubmitClicked] = useState(false);
  const [totalCountOfPatients, setTotalCountOfPatients] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [districtParactitionerId, setDistrictPractionerId] = useState(null);
  const [resetSelect, setResetSelect] = useState(false);
  const [displayPagination, setDisplayPagination] = useState(true);
  const userInformation: any = useAppSelector(UserState);
  const userSelectedLanguage: any = useAppSelector(PersistState);
  const breadcrumRedirectUrl =
    location.pathname.indexOf("stateAdmin") === -1
      ? PAGE.GLOBAL_STATE_ADMIN.replace(":state", state || "")
      : PAGE.STATE_ADMIN;
  const stateName: string =
    state?.replace(/%20/g, " ") ||
    userInformation?.userDetails?.user_metadata?.address?.region;

  const mapToGrid = (patientData: any) => {
    if (!patientData) {
      setDisplayPagination(false);
      return [];
    }
    const gridData = patientData.map((data: any) => {
      setDisplayPagination(true);
      const firstName = data?.resource?.name[0]?.given[0];
      const middleName = data?.resource?.name[0]?.given[1]
      const lastName = data?.resource?.name[0]?.family;
      const pName = firstName + ((middleName) ? " " + middleName : "") + " " + lastName;

      return {
        name: pName,
        patientID:
          Utility.getIDString(data?.resource) === "id"
            ? `${i18next.t("unavailable")}`
            : `${i18next.t(Utility.getIDString(data?.resource))} / ` +
              Utility.getIDNumber(data?.resource),
        phoneNumber: data?.resource?.telecom[0]?.value.padStart(13, "+91"),
        cityVillage: data?.resource?.address[0]?.city,
        district: data?.resource?.address[0]?.district,
        patientId: data?.resource?.id,
        apiResponse: data?.resource,
      };
    });
    return gridData;
  };

  useEffect(() => {
    setPageNumber(1);
    setDistrictPractionerId(null);
    formik.setFieldValue("searchInput", "");
    formik.setFieldValue("district", "");
    formik.setFieldValue("cityVillage", "");
    formik.setFieldValue("cityName", "");
    formik.setFieldValue("districtQuery", "");
    formik.setFieldValue("cityQuery", "");
    setCitiesList([]);
    setSubmitClicked(false);
    setResetSelect(!resetSelect);
    const fetchDistricts = async () => {
      try {
        const requestDetails = {
          url: API.DISTRICTS_BY_STATE.replace(":stateName", stateName),
          method: "GET",
        };
        const resp = await dispatch(ApiCallWithLoader(requestDetails));
        setDistricts(
          resp.payload?.entry.map((location: any) => {
            return {
              label: location.resource.name,
              value: location.resource.id,
            };
          })
        );
      } catch (error) {
        dispatch(errorUpdate({ isError: true, error: error }));
      }
    };

    fetchDistricts().then(async () => {
      await fetchPatients(0, "", "", "");
    });
  }, [stateName]);

  useEffect(() => {
    const newRecords = totalRecords.map((data: any) => {
      return {
        ...data,
        patientID:
          Utility.getIDString(data?.apiResponse) === "id"
            ? `${i18next.t("unavailable")}`
            : `${i18next.t(Utility.getIDString(data?.apiResponse))} / ` +
              Utility.getIDNumber(data?.apiResponse),
      };
    });
    setTotalRecords(newRecords);
  }, [userSelectedLanguage.language]);

  const handlePageChange = async (e: any) => {
    if (formik.values.searchInput) {
      setSubmitClicked(true);
      let url = urlQueryCreator(formik.values.searchInput);
      await fetchPatients(
        e.selected,
        url,
        formik.values.districtQuery,
        formik.values.cityQuery,
        formik.values.sortValue,
        formik.values.sortType
      );
    } else {
      await fetchPatients(
        e.selected,
        "",
        formik.values.districtQuery,
        formik.values.cityQuery,
        formik.values.sortValue,
        formik.values.sortType
      );
    }
    setPageNumber(e.selected + 1);
  };

  const handlActionClick = (rowData: any, actionValue: any) => {
    if (actionValue === "viewDetails") {
      dispatch(
        updateBreadcrum({
          routeHistory: [
            {
              label: "Patient",
              label_key: "patient",
              path: `${breadcrumRedirectUrl}/patient`,
              id: "patient",
              value: "",
            },
          ],
          display: true,
        })
      );
      const routeLink = PAGE.VIEW_USERS.replace(":userType", "patient").replace(
        ":userName",
        rowData?.cells[0].data
      );
      navigate(routeLink, {
        state: { patientId: rowData?.cells[6].data },
      });
    }
  };
  const gridColumnHeaders: any[] = [
    {
      id: "name",
      name: i18next.t("grid.patient_name"),

      formatter: (_cell: any, row: any) => {
        const ActionList = row.cells[0].data;
        if (ActionList.length > 26) {
          return GridRender(
            <NameEllipses
              actionList={ActionList}
              id={row.cells[0]._id}
              toggleName={ActionList}
              tabType="patients"
            />
          );
        } else {
          return ActionList;
        }
      },
    },
    { id: "patientID", name: i18next.t("grid.patient_id") },
    {
      id: "phoneNumber",
      name: i18next.t("grid.phone_number"),
    },
    {
      id: "district",
      name: i18next.t("grid.district"),
    },
    {
      id: "cityVillage",
      name: i18next.t("grid.city_village"),
    },
    {
      id: "action",
      name: i18next.t("grid.action"),
      formatter: (_cell: any, row: any) => {
        const ActionList = [
          {
            value: i18next.t("grid.view_details"),
            action: "viewDetails",
          },
        ];
        return GridRender(
          <ActionDropDown
            actionList={ActionList}
            row={row}
            onActionClick={handlActionClick}
            useLeyden={true}
          />
        );
      },
    },
    { id: "patientId", name: "cdrpatientid", hidden: true },
  ];

  const formik = useFormik({
    initialValues: {
      searchInput: "",
      district: "",
      cityVillage: "",
      cityName: "",
      districtQuery: "",
      cityQuery: "",
      sortValue: "name",
      sortType: "asc",
    },
    onSubmit: async () => {},
  });

  async function changeDistrict(selectedDistricts: any) {
    const selectedDistrict = selectedDistricts;
    if (formik.values.district !== selectedDistrict.label) {
      // Clear the city details when the district changes
      formik.setFieldValue("cityVillage", "");
    }
    // Set the district details
    formik.setFieldValue("district", selectedDistrict.value);

    // Populate the cities dropdown
    await getCities(stateName, selectedDistrict.label);
  }
  async function getCities(stateName: string, districtName: string) {
    // Update the state name to match the filename structure
    setCitiesList([]);
    if (stateName && districtName) {
      // Load the states and districts from the CSV file
      const getCitiesCountUrl = API.CITY_VILLAGE_BY_DISTRICTS.replace(
        ":state",
        stateName?.toLowerCase()
      ).replace(":district", districtName?.toLowerCase());
      const countRequestDetails = {
        url: `${getCitiesCountUrl}&_count=1`,
        method: "GET",
      };
      const getCityDataCount = await dispatch(
        ApiCallWithLoader(countRequestDetails)
      );
      const total_count = getCityDataCount?.payload?.total;
      const roleRequest_url = API.CITY_VILLAGE_BY_DISTRICTS.replace(
        ":state",
        stateName?.toLowerCase()
      ).replace(":district", districtName?.toLowerCase());
      const requestDetails = {
        url: `${roleRequest_url}&_count=${total_count}`,
        method: "GET",
      };

      const getCityData = await dispatch(ApiCallWithLoader(requestDetails));
      const cities = await Locations.getCityMapping(
        getCityData?.payload?.entry
      );

      setCitiesList(cities);
    }
  }
  const memoDataGrid = useMemo(() => {
    return <DataGrid columnData={gridColumnHeaders} cellData={totalRecords} />;
  }, [totalRecords, totalCountOfPatients, userSelectedLanguage?.language]);

  const sortDropDownChange = async (e: any) => {
    setPageNumber(1);
    const tempSortValue = e?.value ? e?.value : "name";
    formik.setFieldValue("sortValue", tempSortValue);
    if (formik.values.searchInput) {
      setSubmitClicked(true);
      let url = urlQueryCreator(formik.values.searchInput);
      await fetchPatients(
        0,
        url,
        formik.values.districtQuery,
        formik.values.cityQuery,
        tempSortValue,
        formik.values.sortType
      );
    } else {
      await fetchPatients(
        0,
        "",
        formik.values.districtQuery,
        formik.values.cityQuery,
        tempSortValue,
        formik.values.sortType
      );
    }
  };
  const onSearch = async (searchText: string) => {
    setPageNumber(1);
    formik.setFieldValue("searchInput", searchText);
    setSubmitClicked(true);
    let url = urlQueryCreator(searchText);
    await fetchPatients(
      0,
      url,
      formik.values.districtQuery,
      formik.values.cityQuery,
      formik.values.sortValue,
      formik.values.sortType
    );
  };
  const applyFilter = async () => {
    setPageNumber(1);
    let districtName;
    try {
      if (formik.values.district) {
        let locationID = `?location=LOCATION/${formik.values.district}`;
        let url = API.PRACTITIONER_ROLES_ID + locationID;
        const requestDetails = {
          url,
          method: "GET",
        };
        const searchResult = await dispatch(ApiCallWithLoader(requestDetails));
        let practitionerID = searchResult?.payload?.entry[0]?.resource?.id;
        setDistrictPractionerId(practitionerID);
        districtName =
          practitionerID !== ""
            ? `&general-practitioner=PractitionerRole/${practitionerID}`
            : "";
      } else {
        districtName = "";
      }
      let city = formik.values.cityVillage
        ? `&address-city=${formik.values.cityName}`
        : "";
      formik.setFieldValue("cityQuery", city);
      formik.setFieldValue("districtQuery", districtName);
      if (formik.values.searchInput) {
        setSubmitClicked(true);
        let url = urlQueryCreator(formik.values.searchInput);
        await fetchPatients(
          0,
          url,
          districtName,
          city,
          formik.values.sortValue,
          formik.values.sortType
        );
      } else {
        await fetchPatients(
          0,
          "",
          districtName,
          city,
          formik.values.sortValue,
          formik.values.sortType
        );
      }
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };
  const sortOrderChange = async (e: any) => {
    setPageNumber(1);
    const finalSortValue = formik.values.sortType === "asc" ? "dec" : "asc";
    formik.setFieldValue("sortType", finalSortValue);
    if (formik.values.searchInput) {
      setSubmitClicked(true);
      let url = urlQueryCreator(formik.values.searchInput);
      await fetchPatients(
        0,
        url,
        formik.values.districtQuery,
        formik.values.cityQuery,
        formik.values.sortValue,
        finalSortValue
      );
    } else {
      await fetchPatients(
        0,
        "",
        formik.values.districtQuery,
        formik.values.cityQuery,
        formik.values.sortValue,
        finalSortValue
      );
    }
  };

  const urlQueryCreator = (searchInput: any) => {
    const searchText = searchInput?.trim();
    let urlQuery = "";
    if (
      searchText.length > 1 &&
      !isNaN(searchText) &&
      searchText.length == 10 //Use Phone as Search Parameter only for 10 digits
    ) {
      urlQuery = "phone=" + searchText;
    } else {
      let querySplit = searchText.split(" ");
      urlQuery =
        "name:contains=" +
        querySplit[0] +
        "," +
        querySplit[querySplit.length - 1];
    }
    setSearchString(urlQuery);
    return urlQuery;
  };

  const fetchPatients = async (
    pageNumber: number,
    searchQuerry = "",
    district: string,
    village = "",
    sortValue = "given",
    sortType = "asc"
  ) => {
    sortValue = sortValue === "name" ? "given" : sortValue;

    if (sortType == "asc") {
      sortValue =
        sortValue === "_lastUpdated"
          ? `&_sort=-${sortValue}`
          : `&_sort=${sortValue}`;
    } else {
      sortValue =
        sortValue === "_lastUpdated"
          ? `&_sort=${sortValue}`
          : `&_sort=-${sortValue}`;
    }
    try {
      let url =
        API.SEARCH +
        `?address-state=${stateName}` +
        `&${searchQuerry}` +
        `&_page=${pageNumber + 1}&_count=10&_getpagesoffset=${
          pageNumber * 10
        }` +
        district +
        village +
        sortValue;
      url = pageNumber === 0 ? url + "&_total=accurate" : url;
      const requestDetails = {
        url,
        method: "GET",
      };
      const fetchPatientDetails = await dispatch(
        ApiCallWithLoader(requestDetails)
      );
      const patientData = mapToGrid(fetchPatientDetails?.payload?.entry);
      setTotalRecords(patientData);

      if (pageNumber === 0) {
        setTotalCountOfPatients(fetchPatientDetails?.payload?.total);
      }
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  const onInput = async (searchText: any) => {
    if (searchText.length === 0) {
      setPageNumber(1);
      formik.setFieldValue("searchInput", "");
      await fetchPatients(
        0,
        "",
        formik.values.districtQuery,
        formik.values.cityQuery,
        formik.values.sortValue,
        formik.values.sortType
      );
    }
  };
  return (
    <div className="container">
      <div className="patientDetails">
        <div className="ashaDetails">
          <AdminSearch
            placeholderKey="placeholder.search_patients"
            onSearch={onSearch}
            onInput={onInput}
          />
          <div className="applyContainer">
            <LeydenSelect
              label={"Select District"}
              label_key="select_district"
              options={Utility.getOptions(districts ? districts : [])}
              onChange={(district: any) => {
                changeDistrict(district);
              }}
              id={"districtList"}
              defaultSelected={formik.values.district}
              error={formik.errors.district}
              mandate={false}
              blankOptionLabel={"All"}
            />
            <LeydenSelect
              label={"City/Town/Village"}
              label_key="city_village"
              options={Utility.getOptions(citiesList ? citiesList : [])}
              onChange={(cities: any) => {
                formik.setFieldValue("cityName", cities?.label);
                formik.setFieldValue("cityVillage", cities?.value);
              }}
              id={"cityVillage"}
              defaultSelected={formik.values.cityVillage}
              error={formik.errors.cityVillage}
              mandate={false}
              blankOptionLabel={"All"}
            />
            <Button
              className="btn-outline apply_filter_admin"
              onClick={applyFilter}
              data-testid="apply_filter"
            >
              <Trans i18nKey={"apply_filter"} />
            </Button>
          </div>
        </div>
      </div>
      <div className="listSortAshaDetails">
        <h3>
          <Trans i18nKey={"list_of_patient_in"} /> {stateName}
        </h3>
        <div className="asha_sort_add">
          <span className="adminSortTitle">
            <Trans i18nKey={"sort_by"} />
          </span>

          <LeydenSelect
            label={" "}
            options={Utility.getOptions(SORT_LIST_BENIFICIARY)}
            onChange={(e: any) => {
              sortDropDownChange(e);
            }}
            id={"sort_patients"}
            defaultSelected={formik.values.sortValue}
            error={formik.errors.sortValue}
            mandate={false}
            blankOptionLabel={"name"}
            isLabelInline={true}
          />
          <img
            src="/icons/SortAsc.svg"
            alt="sort_icon"
            onClick={sortOrderChange}
            className={
              formik.values.sortType === "asc"
                ? "admin-asc-img"
                : "admin-dec-img"
            }
          />
        </div>
      </div>
      <div>{memoDataGrid}</div>
      <div className="mt-2" style={{ marginBottom: "150px" }}>
        {displayPagination === true && totalCountOfPatients > 0 && (
          <Pagination
            totalResultsFound={totalCountOfPatients}
            totalRecords={totalRecords}
            pageNumber={pageNumber}
            pageCount={Math.ceil(totalCountOfPatients / 10)}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
export default ErrorBoundaryHandler(PatientsDetails);
